<template>
  <transition name="modal">
    <div class="modal-mask">
      <div class="modal-wrapper" :style="customWrapperStyles">
        <div class="modal-container" :style="customStyles" :class="classes">
          <div class="modal-header" :class="classes" v-if="showHeader">
            <span
              class="close-button editorial"
              :class="classes"
              @click="hideModal"
              v-if="showXButton"
              >x</span
            >
            <slot name="header"></slot>
          </div>

          <div class="modal-body">
            <slot name="body"></slot>
          </div>

          <div class="modal-footer" v-if="showFooter">
            <slot name="footer">
              <button
                class="button-gemnote button-gemnote--small"
                :class="classes"
                @click="confirmAction"
                :disabled="isConfirmButtonActive"
              >
                <span>{{ buttonLabel }}</span>
              </button>
            </slot>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'Modal',
  props: {
    buttonLabel: {
      type: String,
      default: 'OK'
    },
    customWrapperStyles: { type: Object, default: () => ({}) },
    customStyles: {
      type: Object,
      default: () => ({})
    },
    showFooter: {
      type: Boolean,
      default: true
    },
    showHeader: {
      type: Boolean,
      default: true
    },
    showXButton: {
      type: Boolean,
      default: true
    },
    colorVariation: {
      type: String,
      default: 'tertiary',
      validator(val) {
        return [
          'primary',
          'secondary',
          'tertiary-muted',
          'secondary-red',
          'tertiary',
          'abstract',
          'coast',
          'abstract-muted'
        ].includes(val)
      }
    },
    isSelectOpen: {
      type: Boolean,
      default: false
    },
    isConfirmButtonActive: {
      type: Boolean,
      default: true
    }
  },

  computed: {
    classes() {
      const isCropper = this.isCropper ? 'image-cropper' : ''
      return `${isCropper} ${this.colorVariation}`
    }
  },

  methods: {
    hideModal() {
      this.$store.dispatch('modal/hideModal')
      this.$emit('hideModal')
    },
    confirmAction() {
      this.$emit('confirmAction')
    }
  }
}
</script>

<style scoped lang="scss">
@import '../../global.scss';

.modal-mask {
  position: fixed;
  z-index: 9998;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: table;
  transition: opacity 0.3s ease;
  transform: scale(1);
}

.modal-wrapper {
  display: table-cell;
  vertical-align: middle;
}

.modal-container {
  width: 600px;
  min-width: 450px;
  margin: 0px auto;
  padding: 45px;
  background-color: #fff;
  border-radius: 2px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.33);
  transition: all 0.3s ease;
  animation: blowUpModal 0.9s cubic-bezier(0.165, 0.84, 0.44, 1) forwards;

  &.secondary {
    border: 1px solid #000000;
    background: #e6ba5d;
    border-radius: 26px;
  }

  &.tertiary {
    background: #514d43;
    /* border: 1px solid #000000; */
    border-radius: 26px;
  }

  &.abstract {
    background: #dfdad2;
    border: 1px solid #232220;
    border-radius: 26px;
  }

  &.abstract-muted {
    background: #f5f2ee;
    border: 1px solid #232220;
    border-radius: 26px;
  }

  &.coast {
    background: $coast;
    border: 1px solid #232220;
    border-radius: 26px;
  }

  &.image-cropper {
    width: 786px;
    padding: 0px;
  }
  @media only screen and (max-width: 600px) {
    &.abstract {
      overflow: auto;
      height: 100%;
    }
    &.abstract-muted {
      overflow: auto;
      // height: 100%;
    }
  }
}

.modal-header {
  padding: 1rem 3rem 1rem 3rem;
  font-size: 2rem;
  font-family: $editorial !important;
  font-weight: normal;
  margin-bottom: 1rem;
  position: relative;

  h4 {
    font-size: 2rem;
    font-family: $editorial;
    font-weight: normal;
    line-height: 35px;

    &.secondary {
      color: $onyx;
    }

    &.tertiary {
      font-family: $editorial;
      font-size: 2rem;
      color: #24292c;
    }
  }
}

.modal-header {
  padding: 1rem 3rem 1rem 3rem;

  &.image-cropper {
    padding: 3.5rem 5.5rem 1rem 5.5rem;
  }
}

.close-button {
  position: relative;
  top: -40px;
  right: -50px;
  border: none;
  display: inline-block;
  padding: 0px 0px;
  float: right;
  vertical-align: middle;
  overflow: hidden;
  text-decoration: none;
  color: inherit;
  background-color: inherit;
  text-align: center;
  cursor: pointer;
  white-space: nowrap;
  font-size: 1.5rem;

  &.tertiary {
    color: $main-white;
  }
}

.modal-body {
  line-height: 1.5rem;
}

.modal-enter {
  opacity: 0;
}

.modal-leave-active {
  opacity: 0;
}

.modal-enter .modal-container,
.modal-leave-active .modal-container {
  -webkit-transform: scale(1.1);
  transform: scale(1.1);
}

.modal-footer {
  margin-top: 2rem;
  display: flex;
  justify-content: center;

  .button-gemnote {
    font-weight: bold;
    width: auto;
    padding: 0 2.25rem !important;

    &.tertiary {
      background: $main-white;
      border: 1px solid $main-white;
      color: $onyx;
    }

    &[disabled] {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}

@media (max-width: 800px) {
  .modal-container {
    width: 100% !important;
    min-width: 100%;
    padding: 45px 20px;
  }
}

@media (max-width: 575.98px) {
  .modal-container {
    padding: 20px !important;
    width: 100% !important;
    min-width: 100%;
    border-radius: 0 !important;

    &.abstract-muted {
      border-radius: 26px !important;
    }
  }

  .abstract {
    .modal-body {
      overflow-x: hidden !important;
      overflow-y: auto;
      height: 40rem;
    }
  }

  .abstract-muted {
    .modal-body {
      overflow-x: hidden !important;
      overflow-y: auto;
      height: 50rem;
    }
  }
}

@media (min-width: 576px) and (max-width: 767.98px) {
  .modal-container {
    padding: 20px auto !important;
    width: 100% !important;
    min-width: 100%;
    border-radius: 0 !important;
  }

  .abstract {
    .modal-body {
      overflow-x: hidden !important;
      overflow-y: auto;
      height: 40rem;
    }
  }

  .abstract-muted {
    .modal-body {
      overflow-x: hidden !important;
      overflow-y: auto;
      height: 40rem;
    }
  }
}
</style>
